import { Editor_Words } from "@/utils/lang/editor";
import { Button, Center, Flex } from "@chakra-ui/react";
import { langStore, translate, useSelecting } from "@noli/business";
import { checkAIMeta, checkMeta, toast, toFixed, useOwnToast } from "@/utils/misc";

import React, { useEffect, useMemo } from "react";
import { tasksStore } from "@/stores/tasks";
import { SafeOp } from "@/utils/safeOp";
import { SUPER_RESOLUTION_MAX_WH, SUPER_RESOLUTION_POST_MAX_WH } from "@/utils/constants";
import { Actions } from "@/utils/api";
import { observer } from "mobx-react-lite";
import { APIs_Img2Img } from "@/modules/editor/request/api";
import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import { ModelSwitch } from "../../components/common/ModelSwitch";
import { setModelType, statusStore } from "@/stores/status";
import { InfoText, LabelText } from "../../components/styleComponents/Texts";
import { MODEL_TYPES_INFO, NoliboxModelTypes } from "../../constant";
import useImageWithPlaceholder from "../../hooks/useImageWithPlaceholder";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { GenerateBizType } from "@/types/creator";
import { GeneratePriceRules } from "../../hooks/useGenerateCost";

const SR_SUPPORT_MODELS: Extract<NoliboxModelTypes, "nolibox" | "anime">[] = ["nolibox", "anime"];
const ImageProcess: React.FC = () => {
  const toastFn = useOwnToast();
  const lang = langStore.tgt;
  const info = useSelecting("basic")({ fixed: 0 });
  const { modelType } = statusStore;
  const { type, displayNode, imgWH } = info ?? {};
  const isAnime = modelType === "anime";
  const { url, placeholder, enable: enableSuperResolution, meta } = useImageWithPlaceholder();
  const alias = displayNode?.alias;

  useEffect(() => {
    if (meta && checkAIMeta(meta) && meta.data.is_anime) {
      setModelType("anime");
    } else {
      setModelType("nolibox");
    }
  }, [url]);

  const resolution = useMemo(() => {
    if (!imgWH || !displayNode) {
      return {
        sizeStirng: translate(Editor_Words["general-loading"], lang),
        wh: { w: 0, h: 0 },
      };
    }
    const wh = { w: imgWH.w, h: imgWH.h };
    if (wh.w * wh.h > SUPER_RESOLUTION_POST_MAX_WH ** 2) {
      const { w, h } = displayNode.wh;
      if (w * h < wh.w * wh.h) {
        wh.w = w;
        wh.h = Math.abs(h);
      }
    }
    return {
      sizeStirng: `${(wh.w * 4).toFixed(0)} x ${(wh.h * 4).toFixed(0)} px`,
      wh: { w: +(wh.w * 4).toFixed(0), h: +(wh.h * 4).toFixed(0) },
    };
  }, [imgWH, lang]);

  // const { cost, affordable } = useGenerateCost({
  //   generateCount: 1,
  //   calcPriceProps: {
  //     fixPrice: 2,
  //   },
  // });

  async function superResolution(isAnime: boolean): Promise<void> {
    // sanity check
    if (!alias) {
      toast(toastFn, "warning", translate(Editor_Words["no-selecting-node-message"], lang));
      return;
    }
    if (displayNode.type !== "image") {
      toast(
        toastFn,
        "error",
        `${translate(Editor_Words["node-type-error-message"], lang)}(${displayNode.type})`,
      );
      return;
    }

    // setup
    const srcInfo = await SafeOp.getSuitableSrcInfo(
      toastFn,
      lang,
      displayNode,
      SUPER_RESOLUTION_MAX_WH,
      async (wh) => {
        const { w, h } = wh!;
        const virtualInfo = { w: toFixed(w, 2), h: toFixed(h, 2) };
        return SafeOp.setupPostCreator(toastFn, APIs_Img2Img.sr, lang, [alias], virtualInfo);
      },
      (virtualNodeInfo) => async () => {
        await SafeOp.cleanupSetup([alias], virtualNodeInfo);
      },
    );
    if (!srcInfo) return;
    // execute
    await Actions.importMeta({
      t: toastFn,
      lang,
      type: "sr",
      meta: {
        type: "sr",
        data: {
          url: srcInfo.src,
          wh: srcInfo.wh,
          max_wh: SUPER_RESOLUTION_POST_MAX_WH,
          is_anime: isAnime,
          from: checkMeta(displayNode.meta) ? displayNode.meta : undefined,
        },
      },
      setupInfo: {
        checkBusy: false,
        busyAliases: [alias],
        virtualNodeInfo: srcInfo.virtualNodeInfo,
      },
      opt: null,
    });
  }

  return (
    <SubWithTitle title={translate(Editor_Words["image-process-title"], lang)}>
      <LabelText mt="16px">
        {translate(Editor_Words["choose-super-resolution-source"], lang)}
      </LabelText>
      <ImageWithPlaceHolder mt="1em" mx="auto" url={url} placeholderText={placeholder} />
      <ModelSwitch direction="column" enableModelTypes={["nolibox", "anime"]} mt="1em" />

      {enableSuperResolution ? (
        <Flex flexDirection="column" gap="12px" mt="16px">
          {statusStore.modelType ? (
            <InfoText>
              {translate(Editor_Words["model"], lang)}：
              {translate(
                Editor_Words[MODEL_TYPES_INFO[statusStore.modelType]?.showInfo as Editor_Words],
                lang,
              )}
            </InfoText>
          ) : null}
          <InfoText>
            {translate(Editor_Words["resolution"], lang)}： {resolution.sizeStirng}
          </InfoText>
        </Flex>
      ) : null}

      <BizGenerateButton
        sticky
        mt="16px"
        pb="32px"
        size={resolution.wh ?? { w: 0, h: 0 }}
        checkBusyConfig={{
          busyAlias: alias,
          checkAll: true,
        }}
        calcPriceProps={GeneratePriceRules[GenerateBizType.SuperRsolution]}
        buttonProps={{
          isDisabled: type !== "image",
        }}
        onSubmit={() => superResolution(isAnime)}
      >
        {translate(Editor_Words["super-resolution"], lang)}
      </BizGenerateButton>
    </SubWithTitle>
  );
};

export default observer(ImageProcess);
